import React, { useState } from 'react';
import { Mail, Key, ArrowRight, AlertCircle, CheckCircle, RefreshCcw } from 'lucide-react';
import { API_BASE_URL, VERIFY_OTP_URL } from '../config';

const Login = ({ onLoginSuccess }) => {
  const SUPER_ADMIN_EMAILS = ['lokeshchopra150@gmail.com', 'rajesh201rai@gmail.com'];
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState(localStorage.getItem('userEmail') || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const sendOTP = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/sendOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('OTP sent successfully! Check your email.');
        setStep('otp');
      } else {
        setError(data.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please check if server is running.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(VERIFY_OTP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          otp: String(otp).trim(),
        }),
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        try {
          data = await response.json();
        } catch (_) {
          data = {};
        }
      } else {
        const text = await response.text();
        console.error('Verify OTP non-JSON:', response.status, text);
        setError(response.status === 404 ? 'Verify OTP endpoint not found. Check backend URL.' : 'Server error. Please try again.');
        return;
      }

      if (response.ok) {
        // Check if user needs to register as provider
        if (data.needs_registration) {
          setSuccess('OTP verified! Please complete your registration.');
          setTimeout(() => {
            onLoginSuccess(null, false, true, data.email);
          }, 1000);
          return;
        }

        // Store the JWT token and role for Super Admin vs Provider panel
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('isNewUser', data.isNewUser);

        // Force super_admin role for specific admin email
        const normalizedEmail = String(email || '').trim().toLowerCase();
        const userRole = SUPER_ADMIN_EMAILS.includes(normalizedEmail) ? 'super_admin' : data.role;
        if (userRole) localStorage.setItem('adminRole', userRole);

        // Handle store_id (clear for super_admin)
        if (SUPER_ADMIN_EMAILS.includes(normalizedEmail)) {
          localStorage.setItem('adminStoreId', '');
        } else if (data.store_id != null) {
          localStorage.setItem('adminStoreId', data.store_id);
        }

        setSuccess('Login successful! Redirecting...');

        setTimeout(() => {
          onLoginSuccess(data.token, data.isNewUser, false, email);
        }, 1000);
      } else {
        setError(data.error || (response.status === 401 ? 'Invalid OTP.' : 'Verification failed. Please try again.'));
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.message && (err.message.includes('404') || err.message.includes('Failed to fetch')) ? 'Cannot reach server. Check backend is running and CORS.' : 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (resendTimer > 0 || loading) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/resendOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, type: 'admin' }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('A new OTP has been sent to your email.');
        setResendTimer(30); // 30 seconds wait
      } else {
        setError(data.error || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleBackToEmail = () => {
    setStep('email');
    setOtp('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Tartalk Admin</h1>
          <p className="text-gray-600">Sign in to Super Admin or Provider panel</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        )}

        {step === 'email' ? (
          // Email Step
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
            </div>

            <button
              onClick={sendOTP}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  Send OTP
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>
          </div>
        ) : (
          // OTP Step
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
              </label>
              <p className="text-sm text-gray-600 mb-3">
                We sent a 6-digit code to {email}
              </p>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={verifyOTP}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Verify & Login'
                )}
              </button>

              <button
                onClick={handleBackToEmail}
                disabled={loading}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Back to Email
              </button>

              <div className="text-center pt-2">
                <button
                  onClick={resendOTP}
                  disabled={loading || resendTimer > 0}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                >
                  <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure login powered by OTP verification
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;